import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid2,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { styled } from '@mui/material/styles';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useTransactions } from '@/hooks/useTransactions';
import { useUserDetail } from '@/hooks/useUsers';
import { useLedgerAccount, useRampRecipient, useRampLinkedAccount } from '@/hooks/useAccounts';
import { formatOrderDate } from '@liminal/utils/date';
import {
  txContextVariant,
  txStatusVariant,
  directionVariant,
  formatEnumLabel,
  providerLabel,
} from '@/components/ui/statusHelpers';
import { TransactionIdLink } from '@/components/ui/TransactionIdLink';
import { AccountIdLink } from '@/components/ui/AccountIdLink';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { MonoCell } from '@/components/ui/MonoCell';
import type { Transaction } from '@/api/types';

const PAGE_SIZE = 50;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SectionHeading = styled(Typography)({
  marginBottom: 16,
});

/**
 * The entry belonging to this account, which is the side of the transaction
 * this page is about. The remaining entries are the counterparties.
 */
function accountEntry(tx: Transaction, accountId: string) {
  return tx.entries?.find((entry) => entry.accountId === accountId);
}

function counterpartyEntry(tx: Transaction, accountId: string) {
  return tx.entries?.find((entry) => entry.accountId !== accountId);
}

const COLUMN_COUNT = 8;

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const accountId = id ?? '';
  const navigate = useNavigate();
  const isUuid = UUID_PATTERN.test(accountId);

  // A UUID account id belongs to exactly one of: a user, a recipient, or a
  // linked bank account (a system account name is never a UUID). All three
  // lookups fire in parallel; the misses are how the id gets classified, so a
  // failed lookup is informative rather than a failure of this page.
  const uuidLookupId = isUuid ? accountId : '';
  const { data: owner, isLoading: ownerLoading } = useUserDetail(uuidLookupId);
  const { data: recipient, isLoading: recipientLoading } = useRampRecipient(uuidLookupId);
  const { data: linkedAccount, isLoading: linkedAccountLoading } =
    useRampLinkedAccount(uuidLookupId);
  const classifying = isUuid && (ownerLoading || recipientLoading || linkedAccountLoading);

  const { data: account, isLoading: accountLoading } = useLedgerAccount(accountId);

  const pagination = useCursorPagination(PAGE_SIZE);
  const { data, isLoading, isError } = useTransactions({
    accountId,
    pageSize: PAGE_SIZE,
    pageToken: pagination.pageToken,
  });
  const nextPageToken = data?.nextPageToken ?? '';

  // Same on-page sort as the Transactions page: the ledger returns a page
  // ordered by recency and takes no sort parameter. Amounts stay raw decimal
  // strings so the comparator never puts money through a float.
  const { rows: transactions, sort } = useTableSort(
    data?.transactions ?? [],
    {
      id: (tx: Transaction) => tx.id,
      context: (tx: Transaction) => formatEnumLabel(tx.context),
      status: (tx: Transaction) => formatEnumLabel(tx.status),
      direction: (tx: Transaction) => formatEnumLabel(accountEntry(tx, accountId)?.direction),
      amount: (tx: Transaction) => accountEntry(tx, accountId)?.amount,
      assetSymbol: (tx: Transaction) => accountEntry(tx, accountId)?.assetSymbol,
      counterparty: (tx: Transaction) => counterpartyEntry(tx, accountId)?.accountId,
      createdAt: (tx: Transaction) => tx.createdAt,
    },
    'createdAt',
    'desc',
  );

  const accountType = !isUuid
    ? 'System account'
    : classifying
      ? undefined
      : owner
        ? 'User account'
        : recipient
          ? 'Recipient'
          : linkedAccount
            ? 'Linked bank account'
            : 'Internal account';

  const assets = account?.assets ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>

      {/* Account identity & activity */}
      <Card variant="elevated">
          <SectionHeading variant="h6">Account</SectionHeading>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <DetailRow layout="stacked" label="Account ID" value={<CopyableId value={accountId} label="Copy account ID" />} />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <DetailRow layout="stacked" label="Type" value={accountType ?? <Skeleton width={100} height={20} />} />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <DetailRow layout="stacked" label="Transactions" value={(accountLoading ? (
                    <Skeleton width={60} height={20} />
                  ) : (
                    account?.transactionCount
                  )) ?? '—'} />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <DetailRow layout="stacked" label="First Activity" value={(accountLoading ? (
                    <Skeleton width={140} height={20} />
                  ) : account?.firstEntryTime ? (
                    formatOrderDate(account.firstEntryTime)
                  ) : undefined) ?? '—'} />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <DetailRow layout="stacked" label="Last Activity" value={(accountLoading ? (
                    <Skeleton width={140} height={20} />
                  ) : account?.lastEntryTime ? (
                    formatOrderDate(account.lastEntryTime)
                  ) : undefined) ?? '—'} />
            </Grid2>
          </Grid2>
      </Card>

      {/* What the account is: the owning record, per classification */}
      {owner && (
        <Card variant="elevated">
            <SectionHeading variant="h6">User</SectionHeading>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Name" value={<UserIdLink userId={accountId}>
                      {[owner.firstName, owner.lastName].filter(Boolean).join(' ') || 'View user'}
                    </UserIdLink>} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Email" value={owner.email ?? '—'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Phone" value={owner.phoneNumber ?? '—'} />
              </Grid2>
            </Grid2>
        </Card>
      )}

      {recipient && (
        <Card variant="elevated">
            <SectionHeading variant="h6">Recipient</SectionHeading>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Name" value={([recipient.firstName, recipient.lastName].filter(Boolean).join(' ') ||
                    recipient.nickname) ?? '—'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Nickname" value={recipient.nickname ?? '—'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Email" value={recipient.email ?? '—'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Recipient Type" value={formatEnumLabel(recipient.recipientType?.replace('RECIPIENT_TYPE_', ''))} />
              </Grid2>
              {recipient.bankSummary && (
                <>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <DetailRow layout="stacked" label="Bank" value={recipient.bankSummary.bankName ?? '—'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <DetailRow layout="stacked" label="Account" value={(recipient.bankSummary.mask ? `•••• ${recipient.bankSummary.mask}` : undefined) ?? '—'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <DetailRow layout="stacked" label="Currency / Country" value={[recipient.bankSummary.currency, recipient.bankSummary.country]
                        .filter(Boolean)
                        .join(' / ')} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <DetailRow layout="stacked" label="Payment Method" value={recipient.bankSummary.paymentMethod ?? '—'} />
                  </Grid2>
                </>
              )}
              {recipient.walletSummary && (
                <>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <DetailRow layout="stacked" label="Wallet Address" value={recipient.walletSummary.walletAddress ? (
                          <CopyableId
                            value={recipient.walletSummary.walletAddress}
                            label="Copy wallet address"
                          />
                        ) : (
                          '—'
                        )} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <DetailRow layout="stacked" label="Chain" value={recipient.walletSummary.chainName ?? '—'} />
                  </Grid2>
                </>
              )}
              {recipient.liminalUserSummary && (
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DetailRow layout="stacked" label="Liminal User" value={<UserIdLink userId={recipient.liminalUserSummary.liminalUserId}>
                        {recipient.liminalUserSummary.fullName ||
                          recipient.liminalUserSummary.displayTag ||
                          'View user'}
                      </UserIdLink>} />
                </Grid2>
              )}
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Owner" value={<UserIdLink userId={recipient.userId} full />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Created" value={(recipient.createdAt ? formatOrderDate(recipient.createdAt) : undefined) ?? '—'} />
              </Grid2>
            </Grid2>
        </Card>
      )}

      {linkedAccount && (
        <Card variant="elevated">
            <SectionHeading variant="h6">Linked Bank Account</SectionHeading>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Bank" value={(linkedAccount.bankName || linkedAccount.displayName) ?? '—'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Account" value={(linkedAccount.mask ? `•••• ${linkedAccount.mask}` : undefined) ?? '—'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Currency / Country" value={[linkedAccount.currency, linkedAccount.country]
                    .filter(Boolean)
                    .join(' / ')} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Payment Method" value={linkedAccount.paymentMethod ?? '—'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Status" value={formatEnumLabel(
                    linkedAccount.status?.replace('LINKED_ACCOUNT_STATUS_', ''),
                  )} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Provider" value={providerLabel(linkedAccount.provider)} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Owner" value={<UserIdLink userId={linkedAccount.userId} full />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <DetailRow layout="stacked" label="Linked" value={(linkedAccount.createdAt ? formatOrderDate(linkedAccount.createdAt) : undefined) ?? '—'} />
              </Grid2>
            </Grid2>
        </Card>
      )}

      {/* Confirmed per-asset balances */}
      <Card variant="elevated">
          <SectionHeading variant="h6">Balances</SectionHeading>
          {accountLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} height={32} />
              ))}
            </Box>
          ) : assets.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Total In</TableCell>
                  <TableCell>Total Out</TableCell>
                  <TableCell>Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow key={asset.assetSymbol} hover>
                    <TableCell>{asset.assetSymbol}</TableCell>
                    <MonoCell>{asset.balance}</MonoCell>
                    <MonoCell>{asset.totalCredits}</MonoCell>
                    <MonoCell>{asset.totalDebits}</MonoCell>
                    <TableCell>{asset.transactionCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.disabled">
              No confirmed entries for this account.
            </Typography>
          )}
      </Card>

      {/* Ledger activity */}
      <Card variant="elevated" padding="none">
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableCell column="id" sort={sort}>
                ID
              </SortableTableCell>
              <SortableTableCell column="context" sort={sort}>
                Context
              </SortableTableCell>
              <SortableTableCell column="status" sort={sort}>
                Status
              </SortableTableCell>
              <SortableTableCell column="direction" sort={sort}>
                Direction
              </SortableTableCell>
              <SortableTableCell column="amount" sort={sort}>
                Amount
              </SortableTableCell>
              <SortableTableCell column="assetSymbol" sort={sort}>
                Asset
              </SortableTableCell>
              <SortableTableCell column="counterparty" sort={sort}>
                Counterparty
              </SortableTableCell>
              <SortableTableCell column="createdAt" sort={sort}>
                Created At
              </SortableTableCell>
            </TableRow>
          </TableHead>
          <DataTableBody
            columns={COLUMN_COUNT}
            isLoading={isLoading}
            isError={isError}
            isEmpty={transactions.length === 0}
            loadingRows={8}
            emptyMessage="No ledger transactions for this account."
            errorMessage="Could not load this account's transactions."
          >
            {transactions.map((tx) => {
              const entry = accountEntry(tx, accountId);
              const counterparty = counterpartyEntry(tx, accountId);
              return (
                <TableRow key={tx.id} hover>
                  <MonoCell>
                    <TransactionIdLink transactionId={tx.id} />
                  </MonoCell>
                  <TableCell>
                    <StatusPill
                      label={formatEnumLabel(tx.context)}
                      tone={txContextVariant(tx.context)}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      label={formatEnumLabel(tx.status)}
                      tone={txStatusVariant(tx.status)}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>
                    {entry?.direction ? (
                      <StatusPill
                        label={formatEnumLabel(entry.direction)}
                        tone={directionVariant(entry.direction)}
                        size="sm"
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{entry?.amount ?? '—'}</TableCell>
                  <TableCell>{entry?.assetSymbol ?? '—'}</TableCell>
                  <TableCell>
                    <AccountIdLink accountId={counterparty?.accountId} />
                  </TableCell>
                  <TableCell>{tx.createdAt ? formatOrderDate(tx.createdAt) : '—'}</TableCell>
                </TableRow>
              );
            })}
          </DataTableBody>
        </Table>
      </Card>

      {/* No "of N": the ledger returns a cursor and no total. */}
      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={transactions.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={pagination.goToPreviousPage}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={isLoading}
      />
    </Stack>
  );
}
